/**
 * CollapsibleSection Component (US-051)
 *
 * A reusable collapsible section with smooth animations and optional localStorage persistence.
 * Used for grouping related settings/content that can be expanded/collapsed.
 *
 * Features:
 * - Smooth expand/collapse animation using CSS transitions
 * - Optional localStorage persistence for collapse state
 * - Badge support for showing item counts
 * - Icon support for visual categorization
 */
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

export interface CollapsibleSectionProps {
  /** Section title displayed in the header */
  title: string;
  /** Optional icon to display before the title */
  icon?: React.ReactNode;
  /** Initial open state (defaults to false/collapsed) */
  defaultOpen?: boolean;
  /** Content to display when expanded */
  children: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Optional badge to display after the title (e.g., settings count) */
  badge?: React.ReactNode;
  /** Optional localStorage key to persist collapse state */
  storageKey?: string;
  /** Whether this section is nested inside a parent card (removes outer card styling) */
  nested?: boolean;
}

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  icon,
  defaultOpen = false,
  children,
  className = '',
  badge,
  storageKey,
  nested = false,
}) => {
  // Initialize state from localStorage if storageKey is provided
  const getInitialState = (): boolean => {
    if (storageKey) {
      const stored = localStorage.getItem(storageKey);
      if (stored !== null) {
        return stored === 'true';
      }
    }
    return defaultOpen;
  };

  const [isOpen, setIsOpen] = useState(getInitialState);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number>(0);

  // Measure content height for smooth animation
  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [children, isOpen]);

  // Persist state to localStorage when it changes
  useEffect(() => {
    if (storageKey) {
      localStorage.setItem(storageKey, String(isOpen));
    }
  }, [isOpen, storageKey]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  // Use different wrapper styling based on whether it's nested
  const wrapperClass = nested
    ? `border-t border-gray-200 dark:border-gray-700 ${className}`
    : `card ${className}`;

  return (
    <div className={wrapperClass}>
      <button
        onClick={handleToggle}
        className="flex w-full items-center justify-between p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors rounded-t-lg"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-3">
          {icon && <div className="text-gray-600 dark:text-gray-400">{icon}</div>}
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          {badge}
        </div>
        {/* Animated chevron that rotates on expand/collapse */}
        <ChevronDown
          className={`h-5 w-5 text-gray-400 dark:text-gray-500 transition-transform duration-300 ${
            isOpen ? 'rotate-180' : 'rotate-0'
          }`}
        />
      </button>
      {/* Animated content container with smooth height transition */}
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{
          maxHeight: isOpen ? `${contentHeight}px` : '0px',
          opacity: isOpen ? 1 : 0,
        }}
      >
        <div ref={contentRef} className="border-t border-gray-200 dark:border-gray-700 px-6 pb-6 pt-4">
          {children}
        </div>
      </div>
    </div>
  );
};

CollapsibleSection.displayName = 'CollapsibleSection';
